import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Building2, Check, ExternalLink, Loader2, RefreshCw, UserRound, Users } from "lucide-react";
import "./AvatarPicker.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let cachedAvatars = null;

export function useStockAvatars() {
  const [avatars, setAvatars] = useState(cachedAvatars || []);
  const [loading, setLoading] = useState(!cachedAvatars);
  useEffect(() => {
    if (cachedAvatars) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/avatars/stock`);
        cachedAvatars = r.data?.avatars || [];
        setAvatars(cachedAvatars);
      } catch (_) {
        setAvatars([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  return { avatars, loading };
}

/** Team (Social Pro-enabled WMS people for the user's brand/branch) + stock presenters. */
export function usePresenters() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/avatars/presenters${refresh ? "?refresh=1" : ""}`);
      setData(r.data || {});
    } catch (_) {
      setData({ team: [], stock: [], linked: false });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: () => load(true) };
}

const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";

export function AvatarPhoto({ avatar, className = "", style }) {
  if (avatar?.image_url) return <img src={avatar.image_url} alt={avatar.name} loading="lazy" className={className} style={style} />;
  return <span className={`avp-initials ${className}`} style={style} aria-label={avatar?.name}>{initials(avatar?.name)}</span>;
}

function PresenterCard({ a, selected, onChange }) {
  return (
    <button
      type="button"
      className={`avp-card ${selected ? "is-selected" : ""} ${a.kind === "wms" ? "is-team" : ""}`}
      onClick={() => onChange && onChange(a)}
      data-testid={`avatar-option-${a.id}`}
      aria-pressed={selected}
    >
      <div className="avp-photo">
        <AvatarPhoto avatar={a} />
        {selected && <span className="avp-tick"><Check size={13} /></span>}
        {a.is_me && <span className="avp-me" data-testid={`avatar-me-${a.id}`}>You</span>}
      </div>
      <div className="avp-name">{a.name}</div>
      <div className="avp-tag">{a.tagline}</div>
    </button>
  );
}

/**
 * Presenter picker. `value` is the avatar id, `onChange(avatar)`.
 * Team presenters = WMS people with Social Pro enabled for this brand (own branch first); stock avatars as fallback.
 */
export function AvatarPicker({ value, onChange, compact = false, title = "Choose your presenter" }) {
  const { data, loading, reload } = usePresenters();
  const team = data?.team || [];
  const stock = data?.stock || [];
  const where = data?.branch_name || data?.brand_name || "your branch";
  const ownBranch = team.filter((p) => p.own_branch);
  const brandWide = team.filter((p) => !p.own_branch);

  const note = loading ? "" : team.length
    ? `${team.length} Social Pro presenter${team.length === 1 ? "" : "s"} enabled in WMS`
    : data?.wms_down ? "WMS didn't answer — stock presenters only for now"
      : data?.linked ? `No one at ${where} is enabled for Social Pro in WMS yet` : "Stock presenters";

  return (
    <div className={`avp ${compact ? "avp-compact" : ""}`} data-testid="avatar-picker">
      <div className="avp-head">
        <UserRound size={15} />
        <span>{title}</span>
        <span className="avp-note" data-testid="avatar-picker-note">{note}</span>
        {!loading && data?.linked && (
          <button type="button" className="avp-refresh" onClick={reload} title="Refresh from WMS" data-testid="avatar-picker-refresh"><RefreshCw size={12} /></button>
        )}
      </div>
      {loading ? (
        <div className="avp-loading"><Loader2 className="animate-spin" size={16} /> Loading presenters…</div>
      ) : (
        <>
          {ownBranch.length > 0 && (
            <div className="avp-group" data-testid="avatar-group-team">
              <div className="avp-group-label"><Users size={12} /> {data?.branch_name || "Your branch"} · Social Pro</div>
              <div className="avp-grid">{ownBranch.map((a) => <PresenterCard key={a.id} a={a} selected={value === a.id} onChange={onChange} />)}</div>
            </div>
          )}
          {brandWide.length > 0 && (
            <div className="avp-group" data-testid="avatar-group-brand">
              <div className="avp-group-label"><Building2 size={12} /> {data?.brand_name ? `Elsewhere at ${data.brand_name}` : "Other branches"} · Social Pro</div>
              <div className="avp-grid">{brandWide.map((a) => <PresenterCard key={a.id} a={a} selected={value === a.id} onChange={onChange} />)}</div>
            </div>
          )}
          {team.length === 0 && data?.linked && (
            <div className="avp-empty" data-testid="avatar-team-empty">
              Presenters are switched on per person in WMS (Social Pro enabled). Until then, pick a stock presenter.
            </div>
          )}
          <div className="avp-group" data-testid="avatar-group-stock">
            <div className="avp-group-label">Stock presenters</div>
            <div className="avp-grid">{stock.map((a) => <PresenterCard key={a.id} a={a} selected={value === a.id} onChange={onChange} />)}</div>
          </div>
        </>
      )}
    </div>
  );
}

export function AvatarChip({ avatar, size = 26 }) {
  if (!avatar) return null;
  return (
    <span className="avp-chip" data-testid="avatar-chip">
      <AvatarPhoto avatar={avatar} className="avp-chip-photo" style={{ width: size, height: size, fontSize: Math.max(9, size * 0.42) }} />
      <span>{avatar.name}</span>
    </span>
  );
}

/** Editor-side presenter reference: photo, name, where they came from, and a link to the image file. */
export function PresenterCardLarge({ avatar }) {
  if (!avatar) return null;
  const isWms = avatar.kind === "wms";
  return (
    <div className="avp-ref" data-testid="order-presenter-card">
      <div className="avp-ref-photo"><AvatarPhoto avatar={avatar} /></div>
      <div className="avp-ref-body">
        <div className="avp-ref-kicker">Presenter</div>
        <div className="avp-ref-name" data-testid="order-presenter-name">{avatar.name}</div>
        <div className="avp-ref-meta">
          {isWms
            ? `WMS user #${avatar.wms_user_id}${avatar.branch_name ? ` · ${avatar.branch_name}` : ""}${avatar.job_title ? ` · ${avatar.job_title}` : ""}`
            : `Stock avatar · ${avatar.tagline || ""}${avatar.synthesia_avatar_id ? ` · Synthesia ${avatar.synthesia_avatar_id}` : ""}`}
        </div>
        {avatar.image_url && (
          <a className="avp-ref-link" href={avatar.image_url} target="_blank" rel="noreferrer" data-testid="order-presenter-image-link">
            <ExternalLink size={11} /> Open photo
          </a>
        )}
      </div>
    </div>
  );
}
